import React, { useMemo, useState } from 'react';
import { Button, DropdownMenu, Input } from '@spark';

const NONE_VALUE = 'none';
const NONE_LABEL = 'None selected';

const sortAbc = (labels) =>
    [...(labels || [])].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

/**
 * Searchable replace-with select. Task-defined labels appear under
 * "Labels in this task"; other extracted values under "Labels from files".
 * Spark DropdownMenu portals above SparkModalPortal so the list is selectable.
 */
export default function LabelReplacementSelect({
    labelType = 'Label',
    value,
    onChange,
    availableLabels = [],
    taskLabels = null,
    allowNoneOption = false,
    disabled = false,
    onCreateNewLabel,
    showHeader = true,
}) {
    const [open, setOpen] = useState(false);
    const [newLabelValue, setNewLabelValue] = useState('');
    const [labelError, setLabelError] = useState('');

    const options = useMemo(
        () => (Array.isArray(availableLabels) ? availableLabels : []).filter(Boolean),
        [availableLabels],
    );

    const catalog = useMemo(
        () => sortAbc((Array.isArray(taskLabels) ? taskLabels : []).filter(Boolean)),
        [taskLabels],
    );

    const handleAddNewLabel = () => {
        const trimmedValue = (newLabelValue || '').trim();
        setLabelError('');

        if (!trimmedValue) {
            setLabelError(`${labelType} cannot be empty`);
            return;
        }
        if (trimmedValue.includes('.')) {
            setLabelError(`${labelType} cannot contain "." character`);
            return;
        }

        if (typeof onCreateNewLabel === 'function') {
            onCreateNewLabel(trimmedValue);
        }
        if (typeof onChange === 'function') {
            onChange(trimmedValue);
        }
        setNewLabelValue('');
        setLabelError('');
        setOpen(false);
    };

    const { items, sections } = useMemo(() => {
        const makeItem = (opt, displayLabel = opt) => ({
            label: displayLabel,
            state: opt === value ? 'selected' : 'default',
            onClick: () => {
                if (typeof onChange === 'function') {
                    onChange(opt);
                }
                setOpen(false);
            },
        });
        const noneItem = allowNoneOption ? makeItem(NONE_VALUE, NONE_LABEL) : null;
        const catalogSet = new Set(catalog);
        const fileLabels = sortAbc(options.filter((opt) => !catalogSet.has(opt)));

        if (catalog.length === 0) {
            return {
                items: [
                    ...(noneItem ? [noneItem] : []),
                    ...options.map((opt) => makeItem(opt)),
                ],
                sections: undefined,
            };
        }

        const next = [];
        if (noneItem) {
            next.push({ title: '', items: [noneItem] });
        }
        next.push({
            title: 'Labels in this task',
            items: catalog.map((opt) => makeItem(opt)),
        });
        if (fileLabels.length) {
            next.push({
                title: 'Labels from files',
                items: fileLabels.map((opt) => makeItem(opt)),
            });
        }
        return { items: undefined, sections: next };
    }, [allowNoneOption, catalog, options, value, onChange]);

    const displayValue = value === NONE_VALUE ? NONE_LABEL : value;

    return (
        <DropdownMenu
            selectType="single-select"
            size="medium"
            fill
            showLabel={showHeader}
            label={`Replace with ${labelType}`}
            placeholder={`Select ${labelType.toLowerCase()}`}
            text={displayValue || undefined}
            value={displayValue || undefined}
            panelLayout="overlay"
            open={open}
            onOpenChange={setOpen}
            disabled={disabled}
            searchable
            searchMode="trigger"
            searchEmptyText={`No matching ${labelType.toLowerCase()}s`}
            items={items}
            sections={sections}
            footer={(
                <>
                    <Input
                        size="small"
                        showLabel={false}
                        showHelperText={Boolean(labelError)}
                        helperText={labelError || undefined}
                        danger={Boolean(labelError)}
                        placeholder={`New ${labelType.toLowerCase()}`}
                        value={newLabelValue}
                        onChange={(e) => {
                            setNewLabelValue(e.target.value);
                            if (labelError) setLabelError('');
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAddNewLabel();
                            }
                        }}
                    />
                    <Button variant="primary" size="small" onClick={handleAddNewLabel}>
                        Add
                    </Button>
                </>
            )}
        />
    );
}
